import React from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';
import type { SvgIconComponent } from '@mui/icons-material';

type ToggleGroupVariant = 'chip' | 'button';

interface SingleSelectProps {
    options: { icon?: SvgIconComponent; label: string; value: string }[];
    value: string;
    onChange: (value: string) => void;
    multi?: false;
    disabledOptions?: string[];
    variant?: ToggleGroupVariant;
}

interface MultiSelectProps {
    options: { icon?: SvgIconComponent; label: string; value: string }[];
    value: string[];
    onChange: (value: string[]) => void;
    multi: true;
    disabledOptions?: string[];
    variant?: ToggleGroupVariant;
}

type ToggleGroupProps = SingleSelectProps | MultiSelectProps;

export function ToggleGroup(props: ToggleGroupProps) {
    const { options, multi, disabledOptions, variant = 'chip' } = props;

    const isSelected = (optionValue: string) => {
        if (multi) return (props.value as string[]).includes(optionValue);
        return optionValue === props.value;
    };

    const isDisabled = (optionValue: string) => disabledOptions?.includes(optionValue) ?? false;

    const handleClick = (optionValue: string) => {
        if (isDisabled(optionValue)) return;
        if (multi) {
            const current = props.value as string[];
            const onChange = props.onChange as (value: string[]) => void;
            if (current.includes(optionValue)) {
                onChange(current.filter((v) => v !== optionValue));
            } else {
                onChange([...current, optionValue]);
            }
        } else {
            (props.onChange as (value: string) => void)(optionValue);
        }
    };

    return (
        <Container>
            {options.map((option) => (
                <ToggleButton
                    key={option.value}
                    selected={isSelected(option.value)}
                    variant={variant}
                    disabled={isDisabled(option.value)}
                    onClick={() => handleClick(option.value)}
                >
                    {option.icon && <option.icon sx={{ fontSize: 14 }} />}
                    {option.label}
                </ToggleButton>
            ))}
        </Container>
    );
}

const Container = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
});

const ToggleButton = styled('button', {
    shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'variant',
})<{ selected: boolean; variant: ToggleGroupVariant }>(({ selected, variant }) => ({
    fontFamily: 'inherit',
    cursor: 'pointer',
    outline: 'none',
    transition: 'all 0.15s ease',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 4,
    ...(variant === 'button'
        ? {
              minWidth: 68,
              padding: '8px 8px',
              fontSize: 15,
              fontWeight: 600,
              borderRadius: 12,
              border: `1px solid ${selected ? Colors.secondary : Colors.border}`,
              boxShadow: selected ? `inset 0 0 0 1px ${Colors.secondary}` : undefined,
              backgroundColor: selected ? Colors.secondarySoft : Colors.white,
              color: Colors.primary,
              '&:focus, &:focus-visible': {
                  outline: 'none',
              },
              '&:hover': {
                  borderColor: Colors.secondary,
              },
              '&:disabled': {
                  cursor: 'default',
                  color: Colors.textSecondary,
                  backgroundColor: Colors.surface,
                  opacity: 0.6,
                  borderColor: selected ? Colors.secondary : Colors.border,
              },
              '&:disabled:hover': {
                  borderColor: selected ? Colors.secondary : Colors.border,
              },
          }
        : {
              padding: '0px 10px',
              fontSize: 14,
              fontWeight: 500,
              height: 24,
              borderRadius: 30,
              border: selected ? `2px solid ${Colors.turquoise}` : `1px solid ${Colors.lightGrey}`,
              backgroundColor: selected ? Colors.turquoise : Colors.faintGrey,
              color: selected ? Colors.white : Colors.mediumGrey,
              '&:focus, &:focus-visible': {
                  outline: 'none',
                  boxShadow: 'none',
              },
              '&:hover': {
                  borderColor: Colors.turquoise,
              },
              '&:disabled': {
                  cursor: 'default',
                  opacity: 0.85,
                  borderColor: selected ? Colors.turquoise : Colors.lightGrey,
              },
              '&:disabled:hover': {
                  borderColor: selected ? Colors.turquoise : Colors.lightGrey,
              },
          }),
}));
